// @flow
/**
 * Enrollment Actions
 * 
 * Handles the unified onboarding flow: Password Setup + MFA Registration
 * Ports ForgeRock IDM MFA registration logic from Account Settings
 * into the unauthenticated enrollment pipeline.
 * 
 * TRANSACTIONAL GUARANTEES:
 * - No partial onboarding: Flow only completes if ALL steps succeed
 * - Automatic rollback: Failed later steps trigger cleanup of earlier steps
 * - Session locking: User remains in onboarding state until success/explicit cancel
 * 
 * LEGACY SIDE EFFECTS PRESERVED:
 * - Token invalidation via /user/ui/pwd/setfirst API (server-side)
 * - updateLoginMessage dispatch for sign-in page messaging
 * - signInParam (welcome parameter) forwarding
 * - Specific error handling for MGMW_EX_PC and MGMW_EX_1003
 */

import { to } from '../../lib/helpers';
import { push } from 'connected-react-router';
import { REQUEST_TYPES, REQUEST_LOCATIONS } from '../../backend';
import { openModal, closeModal } from '../modal/modalActions';

// LEGACY IMPORT: Required for preserving login page success messaging
import { updateLoginMessage } from '../login/loginActions';
import { selectSignInParamValue } from '../auth/authSelectors';

// LEGACY IMPORT: Required for specific error handling (MGMW_EX_PC, MGMW_EX_1003)
import {
  checkRequestFailure,
  handleRequestFailure,
} from '../../lib/validation';
import { bodyHasError } from '../../lib/validation/serverErrors';

// ============================================================================
// ACTION TYPES
// ============================================================================
export const ENROLLMENT_SET_PASSWORD = '[ENROLLMENT] Set Password';
export const ENROLLMENT_SET_MFA_OPTION = '[ENROLLMENT] Set MFA Option';
export const ENROLLMENT_MFA_INITIATE = '[ENROLLMENT] MFA Initiate';
export const ENROLLMENT_MFA_COMPLETE = '[ENROLLMENT] MFA Complete';
export const ENROLLMENT_COMPLETE = '[ENROLLMENT] Complete';
export const ENROLLMENT_ERROR = '[ENROLLMENT] Error';
export const ENROLLMENT_RESET = '[ENROLLMENT] Reset';
export const ENROLLMENT_ROLLBACK_START = '[ENROLLMENT] Rollback Start';
export const ENROLLMENT_ROLLBACK_COMPLETE = '[ENROLLMENT] Rollback Complete';
export const ENROLLMENT_LOCK = '[ENROLLMENT] Lock';
export const ENROLLMENT_UNLOCK = '[ENROLLMENT] Unlock';

// ============================================================================
// ENROLLMENT STATES (State Machine)
// ============================================================================
export const ENROLLMENT_STATES = {
  IDLE: 'IDLE',
  PASSWORD_SET: 'PASSWORD_SET',
  MFA_SELECTING: 'MFA_SELECTING',
  MFA_INITIATING: 'MFA_INITIATING',
  MFA_COMPLETING: 'MFA_COMPLETING',
  MFA_COMPLETED: 'MFA_COMPLETED',
  FINALIZING: 'FINALIZING',
  COMPLETED: 'COMPLETED',
  ERROR: 'ERROR',
  ROLLING_BACK: 'ROLLING_BACK',
};

// ============================================================================
// ERROR CODES (for specific failure handling)
// ============================================================================
export const ENROLLMENT_ERROR_CODES = {
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  MFA_INITIATE_FAILED: 'MFA_INITIATE_FAILED',
  MFA_COMPLETE_FAILED: 'MFA_COMPLETE_FAILED',
  MFA_OTP_INVALID: 'MFA_OTP_INVALID',
  MFA_WEBAUTHN_CANCELLED: 'MFA_WEBAUTHN_CANCELLED',
  MFA_WEBAUTHN_NOT_SUPPORTED: 'MFA_WEBAUTHN_NOT_SUPPORTED',
  PASSWORD_SAVE_FAILED: 'PASSWORD_SAVE_FAILED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  ROLLBACK_FAILED: 'ROLLBACK_FAILED',
  UNKNOWN: 'UNKNOWN',
};

// ============================================================================
// SESSION STORAGE KEYS (for cross-page state persistence)
// ============================================================================
export const SESSION_KEYS = {
  PASSWORD: 'enrollmentPassword',
  FTOKEN: 'enrollmentFtoken',
  MFA_OPTION: 'enrollmentMFAOption',
  MFA_DATA: 'enrollmentMFAData',
  PHONE_NUMBER: 'enrollmentPhoneNumber',
  // Transaction tracking keys
  STATE: 'enrollmentState',
  DEVICE_UUID: 'enrollmentDeviceUuid',
  TRANSACTION_ID: 'enrollmentTransactionId',
  RETRY_COUNT: 'enrollmentRetryCount',
  LOCK_TIMESTAMP: 'enrollmentLockTimestamp',
  // LEGACY PRESERVATION: Welcome parameter for sign-in redirect
  SIGN_IN_PARAM: 'enrollmentSignInParam',
};

// Maximum retry attempts before forcing reset
const MAX_RETRY_ATTEMPTS = 3;
// Session lock timeout (30 minutes)
const SESSION_LOCK_TIMEOUT_MS = 30 * 60 * 1000;

// ============================================================================
// HELPER: Get enrollment data from session storage
// ============================================================================
export const getEnrollmentData = () => ({
  password: window.sessionStorage.getItem(SESSION_KEYS.PASSWORD),
  ftoken: window.sessionStorage.getItem(SESSION_KEYS.FTOKEN),
  mfaOption: JSON.parse(window.sessionStorage.getItem(SESSION_KEYS.MFA_OPTION) || 'null'),
  mfaData: JSON.parse(window.sessionStorage.getItem(SESSION_KEYS.MFA_DATA) || 'null'),
  phoneNumber: window.sessionStorage.getItem(SESSION_KEYS.PHONE_NUMBER),
  state: window.sessionStorage.getItem(SESSION_KEYS.STATE) || ENROLLMENT_STATES.IDLE,
  deviceUuid: window.sessionStorage.getItem(SESSION_KEYS.DEVICE_UUID),
  transactionId: window.sessionStorage.getItem(SESSION_KEYS.TRANSACTION_ID),
  retryCount: parseInt(window.sessionStorage.getItem(SESSION_KEYS.RETRY_COUNT) || '0', 10),
  lockTimestamp: parseInt(window.sessionStorage.getItem(SESSION_KEYS.LOCK_TIMESTAMP) || '0', 10),
});

// ============================================================================
// HELPER: Update enrollment state
// ============================================================================
const setEnrollmentState = (state: string) => {
  window.sessionStorage.setItem(SESSION_KEYS.STATE, state);
};

// ============================================================================
// HELPER: Clear enrollment session data
// ============================================================================
export const clearEnrollmentSession = () => {
  Object.values(SESSION_KEYS).forEach(key => {
    window.sessionStorage.removeItem(key);
  });
};

// ============================================================================
// HELPER: Increment retry count
// ============================================================================
const incrementRetryCount = (): number => {
  const current = parseInt(window.sessionStorage.getItem(SESSION_KEYS.RETRY_COUNT) || '0', 10);
  const newCount = current + 1;
  window.sessionStorage.setItem(SESSION_KEYS.RETRY_COUNT, String(newCount));
  return newCount;
};

// ============================================================================
// HELPER: Check if session is locked and valid
// ============================================================================
const isSessionLocked = (): boolean => {
  const lockTimestamp = parseInt(window.sessionStorage.getItem(SESSION_KEYS.LOCK_TIMESTAMP) || '0', 10);
  if (!lockTimestamp) return false;
  
  const elapsed = Date.now() - lockTimestamp;
  if (elapsed > SESSION_LOCK_TIMEOUT_MS) {
    // Lock expired, clear it
    window.sessionStorage.removeItem(SESSION_KEYS.LOCK_TIMESTAMP);
    return false;
  }
  return true;
};

// ============================================================================
// HELPER: Acquire session lock
// ============================================================================
const acquireSessionLock = () => {
  window.sessionStorage.setItem(SESSION_KEYS.LOCK_TIMESTAMP, String(Date.now()));
};

// ============================================================================
// HELPER: Release session lock
// ============================================================================
const releaseSessionLock = () => {
  window.sessionStorage.removeItem(SESSION_KEYS.LOCK_TIMESTAMP);
};

// ============================================================================
// API REQUEST BUILDERS
// 
// ARCHITECTURAL NOTE: Token-Bridging Adapter Pattern
// 
// These endpoints delegate to EXISTING authenticated MFA APIs via a thin
// adapter layer (EnrollmentMFAController) that:
// 1. Validates the enrollment token (ftoken)
// 2. Establishes UserContextManager (same as authenticated session)
// 3. Delegates to existing SecurityDeviceService methods
// 
// This avoids duplicating ForgeRock IDM integration code while providing
// secure unauthenticated access during the onboarding flow.
// 
// Backend mapping:
// - /enrollment/mfa/initiate → SecurityDeviceService.initSecurityDeviceRegistration()
// - /enrollment/mfa/complete → SecurityDeviceService.completeSecurityDeviceRegistration()
// - /enrollment/mfa/rollback → SecurityDeviceService.deleteSecurityDevice()
// - /enrollment/phone/sendotp → UserService.sendVerificationCode()
// - /enrollment/phone/verifyotp → UserService.verifyVerificationCode()
// ============================================================================

export const enrollmentMFAInitiateRequest = (body: Object) => ({
  type: `[API] Enrollment MFA Initiate`,
  url: '/user/ui/enrollment/mfa/initiate',
  body,
  APIMethod: REQUEST_TYPES.POST,
  location: REQUEST_LOCATIONS.MGMW,
});

export const enrollmentMFACompleteRequest = (body: Object) => ({
  type: `[API] Enrollment MFA Complete`,
  url: '/user/ui/enrollment/mfa/complete',
  body,
  APIMethod: REQUEST_TYPES.POST,
  location: REQUEST_LOCATIONS.MGMW,
});

/**
 * API: Set enrollment password (validates against IDM policy)
 * Uses existing endpoint - token is NOT invalidated, allowing MFA registration to follow
 */
export const enrollmentSetPasswordRequest = (body: Object) => ({
  type: `[API] Enrollment Set Password`,
  url: '/user/ui/pwd/setfirst',
  body,
  APIMethod: REQUEST_TYPES.POST,
  location: REQUEST_LOCATIONS.MGMW,
});

/**
 * API: Finalize enrollment after MFA registration
 * Invalidates the token and completes onboarding
 */
export const enrollmentFinalizeRequest = (body: Object) => ({
  type: `[API] Enrollment Finalize`,
  url: '/user/ui/enrollment/finalize',
  body,
  APIMethod: REQUEST_TYPES.POST,
  location: REQUEST_LOCATIONS.MGMW,
});

export const enrollmentSendPhoneOTPRequest = (body: Object) => ({
  type: `[API] Enrollment Send Phone OTP`,
  url: '/user/ui/enrollment/phone/sendotp',
  body,
  APIMethod: REQUEST_TYPES.POST,
  location: REQUEST_LOCATIONS.MGMW,
});

export const enrollmentVerifyPhoneOTPRequest = (body: Object) => ({
  type: `[API] Enrollment Verify Phone OTP`,
  url: '/user/ui/enrollment/phone/verifyotp',
  body,
  APIMethod: REQUEST_TYPES.POST,
  location: REQUEST_LOCATIONS.MGMW,
});

/**
 * API: Rollback/delete MFA device registration
 * Called when later steps fail to prevent partial state
 */
export const enrollmentMFARollbackRequest = (body: Object) => ({
  type: `[API] Enrollment MFA Rollback`,
  url: '/user/ui/enrollment/mfa/rollback',
  body,
  APIMethod: REQUEST_TYPES.POST,
  location: REQUEST_LOCATIONS.MGMW,
});

// ============================================================================
// ERROR HANDLING: Create structured error with code
// ============================================================================
const createEnrollmentError = (
  code: string,
  message: string,
  details?: Object
) => ({
  code,
  message,
  details,
  timestamp: new Date().toISOString(),
});

// ============================================================================
// ERROR HANDLING: Map API/system errors to enrollment error codes
// ============================================================================
const mapErrorToCode = (error: any, context: string): string => {
  if (!error) return ENROLLMENT_ERROR_CODES.UNKNOWN;
  
  // Network errors
  if ((error.message && error.message.includes('network')) || (error.message && error.message.includes('fetch'))) {
    return ENROLLMENT_ERROR_CODES.NETWORK_ERROR;
  }
  
  // WebAuthn specific errors
  if (error.name === 'NotAllowedError') {
    return ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_CANCELLED;
  }
  if (error.name === 'NotSupportedError') {
    return ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_NOT_SUPPORTED;
  }
  
  // Context-based mapping
  switch (context) {
    case 'MFA_INITIATE':
      return ENROLLMENT_ERROR_CODES.MFA_INITIATE_FAILED;
    case 'MFA_COMPLETE':
      return ENROLLMENT_ERROR_CODES.MFA_COMPLETE_FAILED;
    case 'PASSWORD_SAVE':
      return ENROLLMENT_ERROR_CODES.PASSWORD_SAVE_FAILED;
    default:
      return ENROLLMENT_ERROR_CODES.UNKNOWN;
  }
};

// ============================================================================
// ERROR HANDLING: Get user-friendly error message
// ============================================================================
const getErrorMessage = (errorCode: string): string => {
  const messages = {
    [ENROLLMENT_ERROR_CODES.TOKEN_INVALID]: 
      'Your enrollment link is invalid. Please request a new enrollment email.',
    [ENROLLMENT_ERROR_CODES.TOKEN_EXPIRED]: 
      'Your enrollment link has expired. Please request a new enrollment email.',
    [ENROLLMENT_ERROR_CODES.MFA_INITIATE_FAILED]: 
      'Failed to start MFA registration. Please try again.',
    [ENROLLMENT_ERROR_CODES.MFA_COMPLETE_FAILED]: 
      'Failed to complete MFA registration. Please try again.',
    [ENROLLMENT_ERROR_CODES.MFA_OTP_INVALID]: 
      'The verification code is incorrect. Please check and try again.',
    [ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_CANCELLED]: 
      'Passkey registration was cancelled. Please try again when ready.',
    [ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_NOT_SUPPORTED]: 
      'Your device does not support passkey authentication. Please choose a different method.',
    [ENROLLMENT_ERROR_CODES.PASSWORD_SAVE_FAILED]: 
      'Failed to save your password. Your MFA registration has been rolled back. Please try again.',
    [ENROLLMENT_ERROR_CODES.NETWORK_ERROR]: 
      'Network connection lost. Please check your connection and try again.',
    [ENROLLMENT_ERROR_CODES.SESSION_EXPIRED]: 
      'Your session has expired. Please start the enrollment process again.',
    [ENROLLMENT_ERROR_CODES.ROLLBACK_FAILED]: 
      'An error occurred while cleaning up. Please contact support.',
    [ENROLLMENT_ERROR_CODES.UNKNOWN]: 
      'An unexpected error occurred. Please try again or contact support.',
  };
  return messages[errorCode] || messages[ENROLLMENT_ERROR_CODES.UNKNOWN];
};

// ============================================================================
// ROLLBACK: Delete registered MFA device from ForgeRock
// Called when password save fails after MFA registration
// ============================================================================
export const rollbackMfaRegistration = () => async (dispatch, getState) => {
  const { ftoken, deviceUuid, mfaOption } = getEnrollmentData();
  
  dispatch({ type: ENROLLMENT_ROLLBACK_START });
  setEnrollmentState(ENROLLMENT_STATES.ROLLING_BACK);

  if (!ftoken || !deviceUuid) {
    // No device to rollback, just reset state
    dispatch({ type: ENROLLMENT_ROLLBACK_COMPLETE, payload: { skipped: true } });
    return Promise.resolve();
  }

  try {
    const [{ response, body }, err] = await to(
      dispatch(enrollmentMFARollbackRequest({
        ftoken,
        deviceUuid,
        deviceTypeCd: (mfaOption && mfaOption.type) || '',
      }))
    );

    if (err || response.status !== 200) {
      // Rollback failed - this is a critical error
      // Log for monitoring but don't block user
      console.error('[ENROLLMENT] MFA rollback failed:', err || body);
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(
          ENROLLMENT_ERROR_CODES.ROLLBACK_FAILED,
          'Failed to rollback MFA registration',
          { deviceUuid, mfaType: (mfaOption && mfaOption.type) }
        ),
      });
      // Still allow user to retry
    }

    // Clear device UUID from session
    window.sessionStorage.removeItem(SESSION_KEYS.DEVICE_UUID);
    
    dispatch({ type: ENROLLMENT_ROLLBACK_COMPLETE, payload: { success: true } });
    return Promise.resolve();
  } catch (error) {
    console.error('[ENROLLMENT] MFA rollback exception:', error);
    dispatch({ type: ENROLLMENT_ROLLBACK_COMPLETE, payload: { success: false, error } });
    return Promise.resolve(); // Don't block user even if rollback fails
  }
};

// ============================================================================
// VALIDATION: Check enrollment prerequisites
// ============================================================================
const validateEnrollmentPrerequisites = (dispatch, requiredFields: string[]) => {
  const data = getEnrollmentData();
  
  // Check session lock timeout
  if (data.lockTimestamp && !isSessionLocked()) {
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(
        ENROLLMENT_ERROR_CODES.SESSION_EXPIRED,
        'Session timed out'
      ),
    });
    dispatch(openModal('EnrollmentErrorModal', {
      errorCode: ENROLLMENT_ERROR_CODES.SESSION_EXPIRED,
      error: getErrorMessage(ENROLLMENT_ERROR_CODES.SESSION_EXPIRED),
      canRetry: false,
    }));
    return { valid: false, data };
  }
  
  // Check required fields
  for (const field of requiredFields) {
    if (!data[field]) {
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(
          ENROLLMENT_ERROR_CODES.TOKEN_INVALID,
          `Missing required field: ${field}`
        ),
      });
      return { valid: false, data };
    }
  }
  
  // Check retry limit
  if (data.retryCount >= MAX_RETRY_ATTEMPTS) {
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(
        ENROLLMENT_ERROR_CODES.UNKNOWN,
        'Maximum retry attempts exceeded'
      ),
    });
    dispatch(openModal('EnrollmentErrorModal', {
      errorCode: ENROLLMENT_ERROR_CODES.UNKNOWN,
      error: 'Too many failed attempts. Please start over or contact support.',
      canRetry: false,
      showStartOver: true,
    }));
    return { valid: false, data };
  }
  
  return { valid: true, data };
};

// ============================================================================
// ACTION CREATORS
// ============================================================================

/**
 * Set enrollment password via API
 * 
 * NEW FLOW:
 * 1. Validates password against ForgeRock IDM policy
 * 2. Sets password in ForgeRock
 * 3. Token remains valid for MFA registration step
 * 4. If password fails policy, returns error for form display
 * 
 * @param {string} password - The new password
 * @param {string} ftoken - The enrollment token
 * @param {Object} formikActions - Formik actions for form error handling
 */
export const setEnrollmentPassword = (password: string, ftoken: string, formikActions?: Object) => async (dispatch, getState) => {
  // Acquire session lock
  acquireSessionLock();
  
  try {
    const [{ response, body }, err] = await to(
      dispatch(enrollmentSetPasswordRequest({
        ftoken,
        password,
        newPass: password,
        lastPasswordUpdateDate: new Date().toISOString(),
      }))
    );
    
    // Handle specific password error codes (MGMW_EX_PC, MGMW_EX_1003)
    if (checkRequestFailure({ response, body })) {
      if (
        bodyHasError(body, { exactError: 'MGMW_EX_PC' }) ||   // Password contains dictionary words
        bodyHasError(body, { exactError: 'MGMW_EX_1003' })    // Password reuse violation
      ) {
        // Password criteria errors - show form-level feedback
        releaseSessionLock();
        if (formikActions) {
          handleRequestFailure(body, formikActions);
        }
        return Promise.reject(new Error('Password does not meet requirements'));
      }
    }
    
    if (err || response.status !== 200) {
      releaseSessionLock();
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(
          ENROLLMENT_ERROR_CODES.PASSWORD_SAVE_FAILED,
          'Failed to set password'
        ),
      });
      
      if (formikActions) {
        formikActions.setErrors({ password: (body && body.message) || 'Failed to set password' });
      }
      return Promise.reject(err || new Error('Failed to set password'));
    }
    
    // Success - store ftoken for MFA step (password is already set on server)
    window.sessionStorage.setItem(SESSION_KEYS.FTOKEN, ftoken);
    window.sessionStorage.setItem(SESSION_KEYS.RETRY_COUNT, '0');
    setEnrollmentState(ENROLLMENT_STATES.PASSWORD_SET);
    
    dispatch({ type: ENROLLMENT_SET_PASSWORD, payload: { ftoken } });
    dispatch({ type: ENROLLMENT_LOCK });
    
    return Promise.resolve({ success: true });
  } catch (error) {
    releaseSessionLock();
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(
        ENROLLMENT_ERROR_CODES.PASSWORD_SAVE_FAILED,
        error.message
      ),
    });
    return Promise.reject(error);
  }
};

/**
 * Store selected MFA option
 */
export const setEnrollmentMFAOption = (mfaOption: Object) => dispatch => {
  window.sessionStorage.setItem(SESSION_KEYS.MFA_OPTION, JSON.stringify(mfaOption));
  setEnrollmentState(ENROLLMENT_STATES.MFA_SELECTING);
  dispatch({ type: ENROLLMENT_SET_MFA_OPTION, payload: mfaOption });
};

// ============================================================================
// AUTHENTICATOR APP (AA) REGISTRATION WITH ERROR HANDLING
// ============================================================================

export const initiateAuthenticatorApp = () => async (dispatch, getState) => {
  const validation = validateEnrollmentPrerequisites(dispatch, ['ftoken']);
  if (!validation.valid) {
    return dispatch(push('/token-error'));
  }
  
  const { ftoken } = validation.data;
  setEnrollmentState(ENROLLMENT_STATES.MFA_INITIATING);

  try {
    const [{ response, body }, err] = await to(
      dispatch(enrollmentMFAInitiateRequest({
        ftoken,
        deviceTypeCd: 'AA',
        deviceUuid: '',
        deviceName: '',
        deviceWebAuthData: '',
      }))
    );

    if (err || response.status !== 200) {
      const errorCode = mapErrorToCode(err, 'MFA_INITIATE');
      incrementRetryCount();
      setEnrollmentState(ENROLLMENT_STATES.ERROR);
      
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(errorCode, 'MFA initiation failed'),
      });
      dispatch(openModal('EnrollmentErrorModal', {
        errorCode,
        error: getErrorMessage(errorCode),
        canRetry: true,
        step: 'MFA_INITIATE',
      }));
      return Promise.reject(err);
    }

    // Extract QR code and device UUID from response
    const qrCodeBlock = body.data.split('    ')[2];
    const qrCodeKey = qrCodeBlock.substring(7, qrCodeBlock.length - 3);
    
    // Store device UUID for potential rollback
    if (body.deviceUuid) {
      window.sessionStorage.setItem(SESSION_KEYS.DEVICE_UUID, body.deviceUuid);
    }
    window.sessionStorage.setItem(SESSION_KEYS.MFA_DATA, JSON.stringify({ qrCode: qrCodeKey }));

    dispatch({ type: ENROLLMENT_MFA_INITIATE, payload: { type: 'AA', qrCode: qrCodeKey } });
    
    return Promise.resolve({ qrCode: qrCodeKey });
  } catch (error) {
    const errorCode = mapErrorToCode(error, 'MFA_INITIATE');
    incrementRetryCount();
    setEnrollmentState(ENROLLMENT_STATES.ERROR);
    
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(errorCode, error.message),
    });
    return Promise.reject(error);
  }
};

export const completeAuthenticatorApp = (otpCode: string) => async (dispatch, getState) => {
  const validation = validateEnrollmentPrerequisites(dispatch, ['ftoken']);
  if (!validation.valid) {
    return Promise.reject(new Error('Invalid enrollment state'));
  }

  const { ftoken } = validation.data;
  setEnrollmentState(ENROLLMENT_STATES.MFA_COMPLETING);

  try {
    const [{ response, body }, err] = await to(
      dispatch(enrollmentMFACompleteRequest({
        ftoken,
        deviceTypeCd: 'AA',
        deviceUuid: '',
        deviceName: '',
        deviceWebAuthData: `{"otpCode":"${otpCode}"}`,
      }))
    );

    if (err || response.status === 400) {
      incrementRetryCount();
      setEnrollmentState(ENROLLMENT_STATES.ERROR);
      
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(
          ENROLLMENT_ERROR_CODES.MFA_OTP_INVALID,
          'Invalid OTP code'
        ),
      });
      dispatch(openModal('MFACodeErrorModal'));
      return Promise.reject(err || new Error('Invalid OTP code'));
    }

    // Store device UUID for potential rollback
    if (body.deviceUuid) {
      window.sessionStorage.setItem(SESSION_KEYS.DEVICE_UUID, body.deviceUuid);
    }

    setEnrollmentState(ENROLLMENT_STATES.MFA_COMPLETED);
    dispatch({ type: ENROLLMENT_MFA_COMPLETE, payload: { type: 'AA', success: true } });
    
    // Proceed to final enrollment completion
    return dispatch(completeEnrollmentWithRollback());
  } catch (error) {
    incrementRetryCount();
    setEnrollmentState(ENROLLMENT_STATES.ERROR);
    
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(
        ENROLLMENT_ERROR_CODES.MFA_COMPLETE_FAILED,
        error.message
      ),
    });
    dispatch(openModal('MFACodeErrorModal'));
    return Promise.reject(error);
  }
};

// ============================================================================
// PASSKEY (YK) REGISTRATION WITH ERROR HANDLING
// ============================================================================

export const initiatePasskey = () => async (dispatch, getState) => {
  const validation = validateEnrollmentPrerequisites(dispatch, ['ftoken']);
  if (!validation.valid) {
    return dispatch(push('/token-error'));
  }

  const { ftoken } = validation.data;
  setEnrollmentState(ENROLLMENT_STATES.MFA_INITIATING);

  try {
    const [{ response, body }, err] = await to(
      dispatch(enrollmentMFAInitiateRequest({
        ftoken,
        deviceTypeCd: 'YK',
        deviceUuid: '',
        deviceName: '',
        deviceWebAuthData: '',
      }))
    );

    if (err || response.status !== 200) {
      const errorCode = mapErrorToCode(err, 'MFA_INITIATE');
      incrementRetryCount();
      setEnrollmentState(ENROLLMENT_STATES.ERROR);
      
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(errorCode, 'Passkey initiation failed'),
      });
      dispatch(openModal('EnrollmentErrorModal', {
        errorCode,
        error: getErrorMessage(errorCode),
        canRetry: true,
      }));
      return Promise.reject(err);
    }

    // Store device UUID for potential rollback
    if (body.deviceUuid) {
      window.sessionStorage.setItem(SESSION_KEYS.DEVICE_UUID, body.deviceUuid);
    }

    const publicKey = eval('(' + body.data.slice(12).slice(0, -3) + ')');
    dispatch({ type: ENROLLMENT_MFA_INITIATE, payload: { type: 'YK', publicKey } });

    // Trigger WebAuthn - this can be cancelled by user
    let credential;
    try {
      credential = await navigator.credentials.create({ publicKey });
    } catch (webAuthnError) {
      const errorCode = mapErrorToCode(webAuthnError, 'MFA_INITIATE');
      incrementRetryCount();
      setEnrollmentState(ENROLLMENT_STATES.ERROR);
      
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(errorCode, webAuthnError.message),
      });
      dispatch(openModal('EnrollmentErrorModal', {
        errorCode,
        error: getErrorMessage(errorCode),
        canRetry: errorCode !== ENROLLMENT_ERROR_CODES.MFA_WEBAUTHN_NOT_SUPPORTED,
      }));
      
      // Rollback the initiated registration
      await dispatch(rollbackMfaRegistration());
      return Promise.reject(webAuthnError);
    }

    // Process credential response
    const rawId = credential.id;
    const clientData = String.fromCharCode.apply(
      null,
      new Uint8Array(credential.response.clientDataJSON)
    );
    const keyData = new Int8Array(credential.response.attestationObject).toString();
    const combinedData = clientData + '::' + keyData + '::' + rawId;

    return dispatch(completePasskey(combinedData));
  } catch (error) {
    const errorCode = mapErrorToCode(error, 'MFA_INITIATE');
    setEnrollmentState(ENROLLMENT_STATES.ERROR);
    
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(errorCode, error.message),
    });
    return Promise.reject(error);
  }
};

export const completePasskey = (webAuthData: string) => async (dispatch, getState) => {
  const validation = validateEnrollmentPrerequisites(dispatch, ['ftoken']);
  if (!validation.valid) {
    return Promise.reject(new Error('Invalid enrollment state'));
  }

  const { ftoken } = validation.data;
  setEnrollmentState(ENROLLMENT_STATES.MFA_COMPLETING);

  try {
    const [{ response, body }, err] = await to(
      dispatch(enrollmentMFACompleteRequest({
        ftoken,
        deviceTypeCd: 'YK',
        deviceUuid: '',
        deviceName: '',
        deviceWebAuthData: webAuthData,
      }))
    );

    if (err || response.status !== 200) {
      incrementRetryCount();
      setEnrollmentState(ENROLLMENT_STATES.ERROR);
      
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(
          ENROLLMENT_ERROR_CODES.MFA_COMPLETE_FAILED,
          'Passkey registration failed'
        ),
      });
      
      // Rollback the initiated registration
      await dispatch(rollbackMfaRegistration());
      return Promise.reject(err);
    }

    // Store device UUID for potential rollback
    if (body.deviceUuid) {
      window.sessionStorage.setItem(SESSION_KEYS.DEVICE_UUID, body.deviceUuid);
    }

    setEnrollmentState(ENROLLMENT_STATES.MFA_COMPLETED);
    dispatch({ type: ENROLLMENT_MFA_COMPLETE, payload: { type: 'YK', success: true } });
    
    return dispatch(completeEnrollmentWithRollback());
  } catch (error) {
    setEnrollmentState(ENROLLMENT_STATES.ERROR);
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(
        ENROLLMENT_ERROR_CODES.MFA_COMPLETE_FAILED,
        error.message
      ),
    });
    return Promise.reject(error);
  }
};

// ============================================================================
// PHONE MFA REGISTRATION WITH ERROR HANDLING
// ============================================================================

export const sendPhoneOTP = (phoneNumber: string, channelCode: string = 'SMS') => async (dispatch, getState) => {
  const validation = validateEnrollmentPrerequisites(dispatch, ['ftoken']);
  if (!validation.valid) {
    return Promise.reject(new Error('Invalid enrollment state'));
  }

  const { ftoken } = validation.data;
  window.sessionStorage.setItem(SESSION_KEYS.PHONE_NUMBER, phoneNumber);
  setEnrollmentState(ENROLLMENT_STATES.MFA_INITIATING);

  try {
    const [{ response, body }, err] = await to(
      dispatch(enrollmentSendPhoneOTPRequest({
        ftoken,
        phoneNumber,
        channelCode,
      }))
    );

    if (err || response.status !== 200) {
      const errorCode = mapErrorToCode(err, 'MFA_INITIATE');
      incrementRetryCount();
      setEnrollmentState(ENROLLMENT_STATES.ERROR);
      
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(errorCode, 'Failed to send OTP'),
      });
      dispatch(openModal('EnrollmentErrorModal', {
        errorCode,
        error: getErrorMessage(errorCode),
        canRetry: true,
      }));
      return Promise.reject(err);
    }

    dispatch({ type: ENROLLMENT_MFA_INITIATE, payload: { type: 'PHONE', phoneNumber, channelCode } });
    return Promise.resolve({ success: true });
  } catch (error) {
    const errorCode = mapErrorToCode(error, 'MFA_INITIATE');
    setEnrollmentState(ENROLLMENT_STATES.ERROR);
    
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(errorCode, error.message),
    });
    return Promise.reject(error);
  }
};

export const verifyPhoneOTP = (otpCode: string) => async (dispatch, getState) => {
  const validation = validateEnrollmentPrerequisites(dispatch, ['ftoken', 'phoneNumber']);
  if (!validation.valid) {
    return Promise.reject(new Error('Invalid enrollment state'));
  }

  const { ftoken, phoneNumber } = validation.data;
  setEnrollmentState(ENROLLMENT_STATES.MFA_COMPLETING);

  try {
    const [{ response, body }, err] = await to(
      dispatch(enrollmentVerifyPhoneOTPRequest({
        ftoken,
        phoneNumber,
        otpCode,
      }))
    );

    if (err || response.status === 400) {
      incrementRetryCount();
      setEnrollmentState(ENROLLMENT_STATES.ERROR);
      
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(
          ENROLLMENT_ERROR_CODES.MFA_OTP_INVALID,
          'Invalid verification code'
        ),
      });
      dispatch(openModal('MFACodeErrorModal'));
      return Promise.reject(err || new Error('Invalid verification code'));
    }

    // Store device UUID for potential rollback
    if (body.deviceUuid) {
      window.sessionStorage.setItem(SESSION_KEYS.DEVICE_UUID, body.deviceUuid);
    }

    setEnrollmentState(ENROLLMENT_STATES.MFA_COMPLETED);
    dispatch({ type: ENROLLMENT_MFA_COMPLETE, payload: { type: 'PHONE', success: true } });
    
    return dispatch(completeEnrollmentWithRollback());
  } catch (error) {
    setEnrollmentState(ENROLLMENT_STATES.ERROR);
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(
        ENROLLMENT_ERROR_CODES.MFA_OTP_INVALID,
        error.message
      ),
    });
    dispatch(openModal('MFACodeErrorModal'));
    return Promise.reject(error);
  }
};

// ============================================================================
// ENROLLMENT COMPLETION WITH AUTOMATIC ROLLBACK
// This is the critical transactional boundary
// ============================================================================

/**
 * Complete enrollment with automatic MFA rollback on failure.
 * 
 * NEW FLOW:
 * Password is already set via setEnrollmentPassword() before MFA registration.
 * This function only needs to finalize the enrollment (invalidate token).
 * 
 * TRANSACTION GUARANTEE:
 * If finalization fails after MFA registration succeeds,
 * the registered MFA device is automatically deleted to
 * prevent partial onboarding state.
 * 
 * LEGACY SIDE EFFECTS PRESERVED:
 * 1. Token invalidation: Handled by /enrollment/finalize endpoint
 * 2. updateLoginMessage: Dispatched immediately on success (not deferred)
 * 3. signInParam: Welcome parameter preserved for redirect
 * 
 * @param {Object} formikActions - Optional Formik actions for legacy form error display
 */
export const completeEnrollmentWithRollback = (formikActions?: Object) => async (dispatch, getState) => {
  const validation = validateEnrollmentPrerequisites(dispatch, ['ftoken']);
  if (!validation.valid) {
    return dispatch(push('/token-error'));
  }

  const { ftoken, mfaOption, deviceUuid } = validation.data;
  setEnrollmentState(ENROLLMENT_STATES.FINALIZING);

  // ============================================================================
  // LEGACY PRESERVATION: Get signInParam for welcome redirect
  // ============================================================================
  const signInParam = selectSignInParamValue(getState());

  try {
    // NEW: Call finalize endpoint instead of password endpoint
    // Password was already set during the password page step
    const [{ response, body }, err] = await to(
      dispatch(enrollmentFinalizeRequest({
        ftoken,
        // Include MFA registration metadata for audit trail
        mfaType: (mfaOption && mfaOption.type) || 'UNKNOWN',
        deviceUuid: deviceUuid || '',
      }))
    );

    if (err || response.status !== 200) {
      // CRITICAL: Finalization failed after MFA registration
      // Must rollback MFA to prevent partial state
      console.error('[ENROLLMENT] Finalization failed, initiating MFA rollback');
      
      await dispatch(rollbackMfaRegistration());
      
      const errorCode = ENROLLMENT_ERROR_CODES.PASSWORD_SAVE_FAILED; // Reuse code for finalization errors
      incrementRetryCount();
      setEnrollmentState(ENROLLMENT_STATES.ERROR);
      
      dispatch({
        type: ENROLLMENT_ERROR,
        payload: createEnrollmentError(errorCode, 'Enrollment finalization failed'),
      });
      dispatch(openModal('EnrollmentErrorModal', {
        errorCode,
        error: 'Failed to complete enrollment. Your MFA registration has been rolled back. Please try again.',
        canRetry: true,
        wasRolledBack: true,
      }));
      
      return Promise.reject(err);
    }

    // ============================================================================
    // SUCCESS: All steps completed
    // ============================================================================
    
    // Release session lock and clear sensitive data
    releaseSessionLock();
    clearEnrollmentSession();
    
    // ============================================================================
    // LEGACY PRESERVATION: Dispatch login success message IMMEDIATELY
    // This ensures the sign-in page shows the success banner even if user
    // navigates directly (matches legacy behavior exactly)
    // ============================================================================
    dispatch(
      updateLoginMessage({
        title: { type: 'SignIn', id: 'Success' },
        info: { type: 'SignIn', id: 'SuccessfullyEnrolled' },
      })
    );
    
    setEnrollmentState(ENROLLMENT_STATES.COMPLETED);
    dispatch({ type: ENROLLMENT_COMPLETE, payload: { success: true } });
    dispatch({ type: ENROLLMENT_UNLOCK });
    
    // ============================================================================
    // LEGACY PRESERVATION: Include signInParam in redirect if present
    // ============================================================================
    // Note: We redirect to success page first; it will forward to sign-in with param
    // Store signInParam for success page to use
    if (signInParam) {
      window.sessionStorage.setItem(SESSION_KEYS.SIGN_IN_PARAM, signInParam);
    }
    
    return dispatch(push('/enrollment-success'));
  } catch (error) {
    // Unexpected error - attempt rollback
    console.error('[ENROLLMENT] Unexpected error during completion:', error);
    
    await dispatch(rollbackMfaRegistration());
    
    setEnrollmentState(ENROLLMENT_STATES.ERROR);
    dispatch({
      type: ENROLLMENT_ERROR,
      payload: createEnrollmentError(
        ENROLLMENT_ERROR_CODES.UNKNOWN,
        error.message
      ),
    });
    return Promise.reject(error);
  }
};

// Legacy export for backward compatibility
export const completeEnrollment = completeEnrollmentWithRollback;

// ============================================================================
// RECOVERY ACTIONS
// ============================================================================

/**
 * Retry the current failed step
 * Maintains session state and increments retry counter
 */
export const retryEnrollmentStep = (step: string) => async (dispatch, getState) => {
  const { retryCount } = getEnrollmentData();
  
  if (retryCount >= MAX_RETRY_ATTEMPTS) {
    dispatch(openModal('EnrollmentErrorModal', {
      errorCode: ENROLLMENT_ERROR_CODES.UNKNOWN,
      error: 'Maximum retry attempts exceeded. Please start over.',
      canRetry: false,
      showStartOver: true,
    }));
    return;
  }
  
  // Clear error state but maintain other session data
  setEnrollmentState(ENROLLMENT_STATES.MFA_SELECTING);
  dispatch({ type: ENROLLMENT_ERROR, payload: null });
  
  // Navigate back to appropriate step
  switch (step) {
    case 'MFA_INITIATE':
    case 'MFA_COMPLETE':
      return dispatch(push('/mfa-registration'));
    case 'PASSWORD_SAVE':
      // Re-attempt completion
      return dispatch(completeEnrollmentWithRollback());
    default:
      return dispatch(push('/mfa-registration'));
  }
};

/**
 * Reset enrollment completely
 * Clears all session data and returns to start
 */
export const resetEnrollment = () => async (dispatch) => {
  // Attempt to rollback any registered MFA
  const { deviceUuid } = getEnrollmentData();
  if (deviceUuid) {
    await dispatch(rollbackMfaRegistration());
  }
  
  // Clear all session data
  clearEnrollmentSession();
  releaseSessionLock();
  
  dispatch({ type: ENROLLMENT_RESET });
  dispatch({ type: ENROLLMENT_UNLOCK });
  
  return dispatch(push('/set-password'));
};

/**
 * Force cancel enrollment
 * Called when user explicitly wants to abandon the process
 */
export const cancelEnrollment = () => async (dispatch) => {
  const { deviceUuid } = getEnrollmentData();
  
  // Attempt cleanup
  if (deviceUuid) {
    await dispatch(rollbackMfaRegistration());
  }
  
  clearEnrollmentSession();
  releaseSessionLock();
  
  dispatch({ type: ENROLLMENT_RESET });
  dispatch({ type: ENROLLMENT_UNLOCK });
  
  // Redirect to home/login
  return dispatch(push('/sign-in'));
};